import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { Loader2, BadgeCheck, Building2, MapPin, Share2, Copy, Download, ExternalLink, QrCode, Home, Users, Sparkles } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PC_MARK } from "@/lib/assets";
import { proShareUrl, qrIdentityUrl, copyToClipboard, openProShare } from "@/lib/share";
import { ShareMenu } from "@/components/ShareMenu";
import { toast } from "sonner";

const NAVY = "#0F2C59", GOLD = "#C79A3B";

export default function MyCard() {
  const nav = useNavigate();
  const { config } = useAuth();
  const [card, setCard] = useState(null);
  const [err, setErr] = useState(false);
  const [qr, setQr] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get("/me/digital-card").then((r) => setCard(r.data)).catch(() => setErr(true));
  }, []);

  useEffect(() => {
    if (!card?.public_id) return;
    QRCode.toDataURL(qrIdentityUrl(card.public_id), {
      margin: 4, width: 640, errorCorrectionLevel: "M",
      color: { dark: NAVY, light: "#FFFFFF" },
    }).then(setQr).catch(() => {});
  }, [card]);

  if (err) return <div className="min-h-screen bg-slate-50"><Navbar /><div className="text-center py-24 text-slate-500">Digital cards are available for professional accounts.</div></div>;
  if (!card) return <div className="min-h-screen bg-slate-50"><Navbar /><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div></div>;

  const slug = card.public_slug;
  const publicUrl = slug ? proShareUrl(slug) : null;
  const enabled = card.public_profile_enabled && slug;

  const copyLink = async () => { if (!publicUrl) return; const ok = await copyToClipboard(publicUrl); toast(ok ? "Profile link copied" : "Copy failed"); };
  const shareCard = () => {
    if (!publicUrl) { toast("Publish your public profile first"); return; }
    openProShare({ url: publicUrl, name: card.name, position: card.sales_position_label || card.role_label, openMenu: () => setShareOpen(true) });
  };
  const downloadQR = () => {
    if (!qr) return;
    const a = document.createElement("a");
    a.href = qr; a.download = `propconnect-qr-${card.public_id}.png`; a.click();
  };

  const org = [card.organization_name, card.organization_branch].filter(Boolean).join(" — ");
  const areas = card.service_areas || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="my-card-page">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0F2C59]">My Digital Card</h1>
            <p className="text-sm text-slate-500 mt-0.5">More than a contact card. A gateway to opportunities.</p>
          </div>
          {enabled ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-xl border-[#0F2C59] text-[#0F2C59]" data-testid="card-view-public" onClick={() => nav(`/pro/${slug}`)}><ExternalLink className="h-4 w-4 mr-1.5" /> View public profile</Button>
              {config?.featured_professional_enabled && (
                <Button className="rounded-xl bg-[#C79A3B] text-white hover:bg-[#C79A3B]/90" data-testid="card-promote" onClick={() => nav("/promote-profile")}><Sparkles className="h-4 w-4 mr-1.5" /> Promote My Profile</Button>
              )}
            </div>
          ) : (
            <Button className="rounded-xl bg-[#C79A3B] text-white" data-testid="card-publish" onClick={() => nav("/profile")}>Publish public profile</Button>
          )}
        </div>

        {!enabled && (
          <div className="mb-5 rounded-xl border border-[#C79A3B]/30 bg-[#C79A3B]/10 px-4 py-3 text-sm text-[#8A6420]" data-testid="card-not-published">
            Your public profile isn't published yet. The card and QR below are a preview — publish from your profile to make them live.
          </div>
        )}

        {/* ===== DIGITAL CARD ===== */}
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-[0_10px_40px_-18px_rgba(15,44,89,0.4)] bg-white" data-testid="digital-card">
          <div className="bg-[#0F2C59] px-6 pt-6 pb-5 text-white relative">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E1B85A]">
              <img src={PC_MARK} alt="PropertyConnect" className="h-4 w-4 object-contain" /> PropertyConnect
            </div>
            <div className="flex items-start gap-4 mt-4">
              {card.avatar ? <img src={card.avatar} alt={card.name} className="h-20 w-20 rounded-2xl object-cover border-2 border-white/20 shrink-0" />
                : <div className="h-20 w-20 rounded-2xl bg-white/10 flex items-center justify-center text-2xl font-bold shrink-0">{(card.name||"?")[0]}</div>}
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-display text-xl font-bold leading-tight">{card.name}</h2>
                  {card.is_verified && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#C79A3B]/20 text-[#E8C878] border border-[#C79A3B]/30"><BadgeCheck className="h-3 w-3" /> Verified</span>}
                </div>
                {card.professional_headline && <p className="text-[#E1B85A] text-sm mt-1 font-medium">{card.professional_headline}</p>}
                {card.sales_position_label && <p className="text-white/90 text-sm mt-1">{card.sales_position_label}</p>}
                {card.role_label && <p className="text-white/70 text-[13px]">{card.role_label}</p>}
              </div>
            </div>
            <div className="mt-4 space-y-1 text-[13px] text-white/85">
              {org && <p className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-[#E1B85A] shrink-0" /> {org}</p>}
              {card.city && <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-[#E1B85A] shrink-0" /> {card.city}</p>}
            </div>
          </div>
          <div className="h-[2px] bg-gradient-to-r from-[#C79A3B] via-[#E1B85A] to-[#C79A3B]" />

          <div className="p-6">
            {areas.length > 0 && (
              <div className="mb-4">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Areas Served</p>
                <div className="flex flex-wrap gap-1.5">
                  {areas.slice(0, 10).map((a) => <span key={a} className="text-[11px] bg-[#0F2C59]/[0.06] text-[#1F3A63] border border-[#0F2C59]/10 px-2 py-0.5 rounded-full">{a}</span>)}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 text-center" data-testid="card-property-count">
                <div className="flex items-center justify-center gap-1.5 text-[#0F2C59]"><Home className="h-4 w-4" /><span className="text-2xl font-bold">{card.public_property_count || 0}</span></div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-0.5">Active Properties</p>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 text-center" data-testid="card-buyer-need-count">
                <div className="flex items-center justify-center gap-1.5 text-[#0F2C59]"><Users className="h-4 w-4" /><span className="text-2xl font-bold">{card.public_buyer_need_count || 0}</span></div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-0.5">Public Buyer Needs</p>
              </div>
            </div>
          </div>
        </div>

        {/* ===== PERMANENT QR ===== */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="qr-section">
          <h3 className="font-display text-lg font-bold text-[#0F2C59] flex items-center gap-2"><QrCode className="h-5 w-5 text-[#C79A3B]" /> Your Permanent PropertyConnect QR</h3>
          <p className="text-sm text-slate-500 mt-1">Share or print this QR. It will continue to open your PropertyConnect professional profile even when your profile information changes.</p>
          <div className="mt-4 flex flex-col sm:flex-row items-center gap-5">
            <div className="rounded-xl border border-slate-200 p-3 bg-white shrink-0">
              {qr ? <img src={qr} alt="Permanent PropertyConnect QR code" className="h-44 w-44" data-testid="qr-image" /> : <div className="h-44 w-44 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>}
            </div>
            <div className="flex-1 w-full space-y-2">
              <code className="block text-xs bg-slate-100 text-slate-600 px-2 py-1.5 rounded break-all">{qrIdentityUrl(card.public_id)}</code>
              <div className="flex flex-wrap gap-2">
                <Button onClick={downloadQR} disabled={!qr} className="rounded-xl bg-[#0F2C59] text-white hover:bg-[#0F2C59]/90" data-testid="download-qr"><Download className="h-4 w-4 mr-1.5" /> Download QR (PNG)</Button>
                <Button onClick={copyLink} variant="outline" className="rounded-xl" data-testid="copy-profile-link" disabled={!publicUrl}><Copy className="h-4 w-4 mr-1.5" /> Copy Profile Link</Button>
                <Button onClick={shareCard} variant="outline" className="rounded-xl" data-testid="share-my-card"><Share2 className="h-4 w-4 mr-1.5" /> Share My Card</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ShareMenu open={shareOpen} onOpenChange={setShareOpen} url={publicUrl} name={card.name} roleText={card.sales_position_label || card.role_label} />
    </div>
  );
}
